import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { CompanyData } from '@/types';
import { StandardizedConnection, convertFileImportsToStandardizedConnections, Connection } from '@/types/connections';
import { Shield, Database, Loader2, Save } from 'lucide-react';
import ConnectorModal from './connectors/ConnectorModal';
import { fetchLinkedConnectors, removeConnector, syncFivetranConnector } from '../utils/api';
import { saveFormDraft, normalizeConnectionForStorage } from '@/utils/organization/saveOrganization';
import { draftTokenField, rememberDraftToken } from '@/utils/onboarding/draftToken';
import { plaidConnectionNeedsReauth } from '@/utils/plaidConnectionStatus';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import DataConnectionFormTooltip from './DataConnectionFormTooltip';
import {
  ANONYMOUS_OWNER,
  getOnboardingSessionOwner,
  readOnboardingOrganisationId,
  noteUnsavedConnectionIds,
  readOwnedConnectionIds,
  readSessionCompanyData,
  sessionHasOwnedConnectionLists,
  writeOwnedConnectionIds,
} from '@/utils/onboarding/sessionState';
import {
  findMissingOwnedConnectionIds,
  listCompanyConnections,
  preserveOwnedConnections,
} from '@/utils/onboarding/connectionFetch';

const companyDataHasConnections = (data: CompanyData | null | undefined): boolean => {
  if (!data?.connections) return false;
  if (Array.isArray(data.connections)) return data.connections.length > 0;
  return typeof data.connections === 'object' && Object.keys(data.connections).length > 0;
};

interface DataConnectionFormProps {
  companyData: CompanyData;
  onBack: () => void;
  onComplete: (linkedConnectors?: StandardizedConnection[]) => void;
  isFinalStep?: boolean;
  organisationId?: string;
  onCompanyDataUpdate?: (data: CompanyData) => void;
}

const DataConnectionForm: React.FC<DataConnectionFormProps> = ({
  companyData,
  onBack,
  onComplete,
  isFinalStep = false,
  organisationId,
  onCompanyDataUpdate
}) => {
  const { toast } = useToast();

  // Detect redirect return synchronously before any useEffect runs to avoid stale UI flash
  const isRedirectReturn =
    sessionStorage.getItem('connectorRedirect') === 'true' &&
    !!sessionStorage.getItem('savedFormStep') &&
    !!sessionStorage.getItem('selectedConnectorId');

  const [isModalOpen, setIsModalOpen] = useState(isRedirectReturn);
  const [linkedConnectors, setLinkedConnectors] = useState<StandardizedConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [isLoadingAfterRedirect, setIsLoadingAfterRedirect] = useState(isRedirectReturn);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [connectionIds, setConnectionIds] = useState<string[]>([]);
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [previousConnectors, setPreviousConnectors] = useState<StandardizedConnection[]>([]);

  const resolveOrganisationId = (): string | undefined => {
    if (organisationId) return organisationId;
    if (companyData.organization_id) return companyData.organization_id;
    return readOnboardingOrganisationId() ?? undefined;
  };

  const withOrganisationId = (data: CompanyData): CompanyData => {
    const id = data.organization_id || resolveOrganisationId();
    return id ? { ...data, organization_id: id } : data;
  };

  const dedupeConnectorsById = (connectors: StandardizedConnection[]): StandardizedConnection[] => {
    const seen = new Map<string, StandardizedConnection>();
    for (const conn of connectors) {
      seen.set(conn.id, conn);
    }
    return Array.from(seen.values());
  };

  const applyFivetranConnectionMap = (connectors: StandardizedConnection[]): StandardizedConnection[] => {
    const connectionMap = JSON.parse(sessionStorage.getItem('connectionMap') || '{}') as Record<string, string>;
    return connectors.map((connector) => {
      if (connector.service === 'plaid') {
        return {
          ...connector,
          connectionId: connector.id,
        };
      }
      const mappedConnectionId = connectionMap[connector.connectorId];
      return {
        ...connector,
        connectionId: mappedConnectionId || connector.connectionId || connector.id,
      };
    });
  };

  const syncSessionConnectionIds = (connectors: StandardizedConnection[]) => {
    const ids = connectors.map((c) => c.id).filter(Boolean);
    const orgId = resolveOrganisationId() ?? null;
    const owned = readOwnedConnectionIds(orgId);
    const previous = new Set([...owned.standard, ...owned.csv]);
    // Ids new to this tab have to be marked before the draft save. A reload that
    // still has connections: [] would otherwise clear them if that save fails.
    noteUnsavedConnectionIds(ids.filter((id) => !previous.has(id)));
    // Stamp ownership alongside the ids so they can never be read back under another organisation.
    writeOwnedConnectionIds(orgId, {
      standard: ids.filter((id) => !id.startsWith('csv-import-')),
      csv: ids.filter((id) => id.startsWith('csv-import-')),
    });
    setConnectionIds(ids);
  };

  // Helper function to convert StandardizedConnection to Connection
  const convertToConnections = (standardizedConnections: StandardizedConnection[]) => {
    return standardizedConnections.map(conn => ({
      id: conn.id,
      name: conn.name,
      type: conn.type,
      status: conn.status,
      created_at: conn.created_at,
      connectionId: conn.connectionId,
      amazon_account_type: conn.amazon_account_type,
      metadata: conn.metadata,
      import_type: conn.import_type,
      ecommerce_subtype: conn.ecommerce_subtype,
      file_name: conn.file_name
    }));
  };

  // Fetch non-CSV file imports only (PDFs, images, documents, etc.)
  const fetchNonCsvFileImports = async (connectionIds: string[]): Promise<StandardizedConnection[]> => {
    try {
      if (connectionIds.length === 0) return [];

      const response = await invokeFunction(supabase, 'fetch-non-csv-file-imports', {
        body: { connectionIds }
      });

      if (response.error) {
        console.error('Error fetching file imports via edge function:', response.error);
        return [];
      }
      if (!response.data?.success) {
        console.error('Edge function returned failure for file imports:', response.data);
        return [];
      }

      const fileImports = response.data?.data || [];
      return convertFileImportsToStandardizedConnections(fileImports);
    } catch (error) {
      console.error('Failed to fetch file imports:', error);
      return [];
    }
  };

  // Helper function to detect newly connected Fivetran connectors
  const detectNewlyConnectedFivetranConnectors = (
    previousConnectors: StandardizedConnection[],
    currentConnectors: StandardizedConnection[]
  ): StandardizedConnection[] => {
    return currentConnectors.filter(current => {
      // Only check Fivetran connectors (not CSV imports or file uploads)
      if (current.service === 'csv' || !current.connectorId || current.connectorId.startsWith('csv-import-')) {
        return false;
      }

      // Find the previous state of this connector
      const previous = previousConnectors.find(prev => prev.connectorId === current.connectorId);

      // If it's a new connector with connected status, or status changed from pending to connected
      return current.status === 'connected' && (!previous || previous.status !== 'connected');
    });
  };

  // Function to trigger sync for newly connected Fivetran connectors
  const triggerSyncForNewConnectors = async (newlyConnectedConnectors: StandardizedConnection[]) => {
    const isConnectorRedirect = sessionStorage.getItem('connectorRedirect') === 'true';

    if (!isConnectorRedirect || newlyConnectedConnectors.length === 0) {
      return;
    }

    for (const connector of newlyConnectedConnectors) {
      try {
        await syncFivetranConnector(connector.connectorId);

      } catch (error) {
        console.error(`Error triggering sync for ${connector.connectorId}:`, error);

      }
    }
  };

  useEffect(() => {
    loadAllConnections()
  }, [toast, organisationId]);

  useEffect(() => {
    const checkRedirectAndConnection = async () => {
      setConnectionsLoaded(false)

      const savedStep = sessionStorage.getItem('savedFormStep');
      const isConnectorRedirect = sessionStorage.getItem('connectorRedirect') === 'true';
      const selectedConnectorId = sessionStorage.getItem('selectedConnectorId');
      const isReconnect = sessionStorage.getItem('isReconnect') === 'true';

      if (savedStep && isConnectorRedirect && selectedConnectorId) {
        setIsLoadingAfterRedirect(true);
        setIsModalOpen(true);

        // Fetch fresh connector status first so we can check whether reauth actually completed
        console.log(`[checkRedirectAndConnection] Fetching fresh connector status | connectorId=${selectedConnectorId} isReconnect=${isReconnect}`);
        const freshConnectors = await loadLinkedConnectors([selectedConnectorId]);
        console.log(`[checkRedirectAndConnection] Fresh connectors loaded | count=${freshConnectors?.length}`);

        // If this was a reconnect attempt, only mark as completed if Fivetran confirms it's healthy
        if (isReconnect) {
          const freshConnector = freshConnectors.find(
            c => c.connectorId === selectedConnectorId || c.id === selectedConnectorId
          );
          // For Amazon connections, the backend always forces needs_reconnect=true until
          // mark-amazon-reauth completes — creating a deadlock. Trust Fivetran's setup_state directly instead.
          const isAmazonService = freshConnector?.service === 'amazon_selling_partner' || freshConnector?.service === 'google_sheets';
          const freshSetupState = (freshConnector as any)?.setup_state ?? (freshConnector?.metadata?.status as any)?.setup_state;
          const isNowHealthy = freshConnector && (
            isAmazonService
              ? freshSetupState === 'connected'
              : !freshConnector.needs_reconnect
          );

          console.log(`[checkRedirectAndConnection] Reconnect check | freshConnector.service=${freshConnector?.service} needs_reconnect=${freshConnector?.needs_reconnect} setup_state=${freshSetupState} isNowHealthy=${isNowHealthy}`);

          if (isNowHealthy) {
            console.log(`[checkRedirectAndConnection] Connector is healthy — calling mark-amazon-reauth`);
            try {
              const reauthOrganisationId = resolveOrganisationId() ?? null;
              await invokeFunction(supabase, 'mark-amazon-reauth', {
                body: {
                  connectorId: selectedConnectorId,
                  organisation_id: reauthOrganisationId,
                  ...draftTokenField(reauthOrganisationId),
                },
              });
              // Reload all connectors so the UI reflects the newly-cleared needs_reconnect status
              // (backend now finds the reauth record and skips the override)
              console.log(`[checkRedirectAndConnection] mark-amazon-reauth succeeded — reloading connectors`);
              await loadAllConnections();
            } catch (err) {
              console.error('Failed to mark Amazon reauth as completed:', err);
            }
          } else {
            console.log(`[checkRedirectAndConnection] Reauth not marked — connector still needs reconnect or was not found`);
          }
          sessionStorage.removeItem('isReconnect');
        }

        setIsLoadingAfterRedirect(false);
        sessionStorage.setItem('connectorRedirect', 'false');
      }
    };

    if (connectionsLoaded)
      checkRedirectAndConnection()
  }, [connectionsLoaded])

  const [connectorIncomplete, setConnectorIncomplete] = useState(false)
  useEffect(() => {
    if (!linkedConnectors?.length) return
    const hasIncompleteConnector = linkedConnectors.some(
      (connector) => connector.status !== 'connected' || plaidConnectionNeedsReauth(connector),
    );
    setConnectorIncomplete(hasIncompleteConnector);
  }, [linkedConnectors])

  const beginConnectionsRefresh = useCallback(() => {
    flushSync(() => {
      setIsLoadingConnections(true);
    });
  }, []);

  const loadAllConnections = async () => {
    setIsLoading(true);
    setIsLoadingConnections(true);
    try {
      // Store previous state for comparison
      setPreviousConnectors([...linkedConnectors]);

      // Only session ids stamped for the active organisation are trusted. Reading them unscoped
      // is what let a previous organisation's connections appear on a brand new form — and then
      // be written onto it on submit.
      const activeOrganisationId = resolveOrganisationId() ?? null;
      const owned = readOwnedConnectionIds(activeOrganisationId);
      const owner = getOnboardingSessionOwner();
      const expectedOwner = activeOrganisationId ?? ANONYMOUS_OWNER;
      // Once this tab has written *both* id lists (including explicit `[]`
      // after a remove), those lists are the working set. A one-sided legacy
      // leftover is not complete — merge companyData so the missing type is
      // not dropped. Merging stale companyData after a full write is what
      // put a just-removed connector back in the modal.
      const trustOwnedSessionIds = owner === expectedOwner && sessionHasOwnedConnectionLists();

      const allConnectionIds: string[] = [...owned.standard, ...owned.csv];

      if (!trustOwnedSessionIds && companyData?.connections) {
        const companyConnections = Array.isArray(companyData.connections)
          ? companyData.connections
          : Object.values(companyData.connections);
        for (const conn of companyConnections) {
          if (conn && typeof conn === 'object' && 'id' in conn) {
            const connection = conn as Connection;
            if (connection.id && !allConnectionIds.includes(connection.id)) {
              allConnectionIds.push(connection.id);
            }
          }
        }
      }

      let fileConnections: StandardizedConnection[] = [];
      const fileImportSourceIds = allConnectionIds.filter((id) => !id.startsWith('csv-import-'));

      if (fileImportSourceIds.length > 0) {
        fileConnections = await fetchNonCsvFileImports(fileImportSourceIds);
      }

      let fetchedConnectors: StandardizedConnection[] = [];
      if (allConnectionIds.length > 0) {
        fetchedConnectors = await fetchLinkedConnectors(allConnectionIds);
      }

      const merged = applyFivetranConnectionMap(
        dedupeConnectorsById([...fileConnections, ...fetchedConnectors]),
      );
      const missingOwnedIds = findMissingOwnedConnectionIds(allConnectionIds, merged);

      // A nonempty-but-partial fetch used to replace owned ids and the draft
      // with only the returned subset, permanently dropping the omitted
      // connections on Finish. Keep the working set unless every owned id resolved.
      if (allConnectionIds.length > 0 && missingOwnedIds.length > 0) {
        const combined = applyFivetranConnectionMap(
          dedupeConnectorsById([
            // The session copy comes first: a bank added just before this refresh
            // is there but not yet in the companyData prop.
            ...preserveOwnedConnections(
              [
                ...listCompanyConnections(readSessionCompanyData(activeOrganisationId)),
                ...listCompanyConnections(companyData),
              ],
              missingOwnedIds,
            ),
            ...merged,
          ]),
        );
        setLinkedConnectors(combined);
        if (combined.length > 0) {
          setHasAttemptedConnection(true);
        }
        const updatedCompanyData = withOrganisationId({
          ...companyData,
          connections: combined.map(normalizeConnectionForStorage),
        });
        sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
        onCompanyDataUpdate?.(updatedCompanyData);
        toast({
          title: "Warning",
          description: "Some data connections could not be loaded. Your saved connections were kept.",
          variant: "destructive"
        });
      } else if (merged.length > 0) {
        setLinkedConnectors(merged);
        syncSessionConnectionIds(merged);
        setHasAttemptedConnection(true);

        const updatedCompanyData = withOrganisationId({
          ...companyData,
          connections: merged.map(normalizeConnectionForStorage),
        });
        sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
        onCompanyDataUpdate?.(updatedCompanyData);
      } else {
        setLinkedConnectors([]);
        // Heal stale companyData only when we had no ids to fetch — a failed
        // fetch of known ids must not wipe the draft.
        if (allConnectionIds.length === 0) {
          syncSessionConnectionIds([]);
          if (companyDataHasConnections(companyData)) {
            const updatedCompanyData = withOrganisationId({
              ...companyData,
              connections: [],
            });
            sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
            onCompanyDataUpdate?.(updatedCompanyData);
          }
        }
      }
      setConnectionsLoaded(true)
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast({
        title: "Warning",
        description: "Could not load existing connections. You may need to reconnect your data sources.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsLoadingConnections(false);
    }
  };

  const loadLinkedConnectors = async (connIds?: string[]): Promise<StandardizedConnection[]> => {
    if (!connIds || connIds.length === 0) {
      return [];
    }
    setIsLoading(true);
    try {
      // Store previous state before loading new connectors
      const prevConnectors = [...linkedConnectors];

      const connectors = await fetchLinkedConnectors(connIds);

      if (Array.isArray(connectors) && connectors.length > 0) {
        const combinedConnectors = [...linkedConnectors];
        connectors.forEach(connector => {
          const existingIndex = combinedConnectors.findIndex(c => c.id === connector.id);
          if (existingIndex >= 0) {
            // Replace with fresh data to clear stale needs_reconnect flags
            combinedConnectors[existingIndex] = connector;
          } else {
            combinedConnectors.push(connector);
          }
        });

        setLinkedConnectors(combinedConnectors);

        // Save connections to database before triggering sync
        try {

          const resolvedOrganisationId = resolveOrganisationId();

          const organisationData = {
            poc_name: companyData.pocName,
            poc_email: companyData.pocEmail,
            poc_phone: companyData.pocPhone || null,
            poc_phone_verified: companyData.pocPhoneVerified || false,
            name: companyData.name,
            name_dbas: companyData.name_dbas || null,
            address: companyData.address || '',
            state_of_incorporation: companyData.stateOfIncorporation || '',
            ein: companyData.ein || '',
            products: companyData.products as unknown as JSON,
            connections: combinedConnectors.map(normalizeConnectionForStorage) as unknown as JSON,
            revenue_last_12_months: companyData.revenueLast12Months || null,
            onboarding_state: 'in_progress' as const, // FIG-345 UC-225
            organization_id: resolvedOrganisationId
          };

          const { data: saved } = await invokeFunction<{ organization_id?: string; draft_token?: string }>(
            supabase,
            'organisations-manager-v3',
            {
              body: {
                action: 'save_draft',
                data: organisationData,
                organization_id: resolvedOrganisationId,
                ...draftTokenField(resolvedOrganisationId),
              },
            },
          );
          rememberDraftToken(saved?.organization_id, saved?.draft_token);
        } catch (error) {
          console.error('Error saving connections to database:', error);
          // Continue with sync even if save fails
        }

        // Check for newly connected Fivetran connectors and trigger sync
        const newlyConnectedFivetranConnectors = detectNewlyConnectedFivetranConnectors(prevConnectors, combinedConnectors);
        if (newlyConnectedFivetranConnectors.length > 0) {
          await triggerSyncForNewConnectors(newlyConnectedFivetranConnectors);
        }

        // Merge into the ids owned by the active organisation, never the raw session keys.
        const orgIdForSession = resolveOrganisationId() ?? null;
        const ownedIds = readOwnedConnectionIds(orgIdForSession);

        const addedIds: string[] = [];
        const newConnIds = [...ownedIds.standard];
        connIds.forEach(connId => {
          if (connId && !newConnIds.includes(connId)) {
            newConnIds.push(connId);
            addedIds.push(connId);
          }
        });

        const newCsvConnIds = [...ownedIds.csv];
        connectors.forEach(connector => {
          const csvId = connector.connectorId;
          if (csvId && (csvId.startsWith('csv-import-') || connector.service === 'csv')) {
            if (!newCsvConnIds.includes(csvId)) {
              newCsvConnIds.push(csvId);
              addedIds.push(csvId);
            }
          }
        });

        noteUnsavedConnectionIds(addedIds);
        writeOwnedConnectionIds(orgIdForSession, { standard: newConnIds, csv: newCsvConnIds });
        setHasAttemptedConnection(true);

        const mappedConnectors = applyFivetranConnectionMap(combinedConnectors);
        syncSessionConnectionIds(mappedConnectors);

        const updatedCompanyData = withOrganisationId({
          ...companyData,
          connections: mappedConnectors.map(normalizeConnectionForStorage),
        });
        saveDraftWithConnections(updatedCompanyData);

        return mappedConnectors;
      }

      return [];
    } catch (error) {
      console.error('Failed to load linked connectors:', error);
      if (hasAttemptedConnection) {
        toast({
          title: "Error",
          description: "Failed to load connected data sources. Please refresh the page.",
          variant: "destructive"
        });
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraftWithConnections = async (updatedCompanyData: CompanyData) => {
    try {
      sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
      await saveFormDraft(updatedCompanyData);
      onCompanyDataUpdate?.(updatedCompanyData);
      toast({
        title: "Draft saved",
        description: "Your connections have been saved to your draft",
        variant: "default"
      });
    } catch (error) {
      console.error('Failed to save draft with connections:', error);
    }
  };

  const persistConnectors = (connectors: StandardizedConnection[], options?: { saveDraft?: boolean }) => {
    const mapped = applyFivetranConnectionMap(dedupeConnectorsById(connectors));
    setLinkedConnectors(mapped);
    syncSessionConnectionIds(mapped);

    const updatedCompanyData = withOrganisationId({
      ...companyData,
      connections: mapped.map(normalizeConnectionForStorage),
    });

    if (options?.saveDraft === false) {
      sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
      onCompanyDataUpdate?.(updatedCompanyData);
    } else {
      void saveDraftWithConnections(updatedCompanyData);
    }
    return mapped;
  };

  const handleUpdateConnectors = (connectors: StandardizedConnection[]) => {
    persistConnectors(connectors);
  };

  const handleRemoveConnector = async (connectorId: string, connectionId: string) => {
    setIsRemoving(connectorId);
    try {
      await removeConnector(connectorId, connectionId, resolveOrganisationId());

      const remaining = linkedConnectors.filter((connector) => {
        const ids = [connector.id, connector.connectorId, connector.connectionId];
        return !ids.some((id) => id === connectorId || id === connectionId);
      });
      persistConnectors(remaining, { saveDraft: false });

      sessionStorage.removeItem('selectedConnectorId');
      sessionStorage.removeItem('selectedConnectorData');
      toast({
        title: "Success",
        description: "Connector removed successfully."
      });
    } catch (error) {
      console.error('Failed to remove connector:', error);
      toast({
        title: "Error",
        description: "Failed to remove connector. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRemoving(null);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      toast({
        title: "Processing",
        description: "Preparing your submission...",
        variant: "default"
      });
      if (onComplete) {
        onComplete(linkedConnectors);
      }
    } catch (error) {
      console.error('Failed to process data:', error);
      toast({
        title: "Error",
        description: "Failed to process your submission. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonText = linkedConnectors.length > 0 ? "Manage" : "Share Data";
  const connectionMessage = linkedConnectors.length > 0 ? "Your data source has been connected successfully. You can manage your connections or add additional sources below." : "Click the button below to select your data source and establish a secure connection.";

  return (
    <div className="form-container">
      <div className="form-section">
        <h2 className="form-header">Connect Your Data</h2>

        <div className="bg-brand-primary bg-opacity-5 p-6 rounded-lg border border-brand-primary border-opacity-20 mb-6">
          <div className="flex items-start">
            <Shield className="text-brand-primary flex-shrink-0 mt-1 mr-4" />
            <div>
              <h3 className="text-lg font-medium text-brand-basic mb-2">Secure Data Connection</h3>
              <p className="text-gray-700 mb-2">Thank you for trusting us with your data.
                We take this responsibility seriously. We use bank-standard encryption and security protocols to ensure your data remains secure.</p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>End-to-end encryption for all data transfers</li>
                <li>Granular access controls</li>
                <li>No persistent storage of sensitive information</li>
              </ul>
              <p className="text-gray-700 mt-3 text-sm">
                We look to stay current with all third party vendor requirements on data collection, confidentiality and storage. If you see service agreements have changed or believe we have overlooked a control please tell us immediately by contacting datasecurity@figabl.com.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-300">
          <Database className="h-12 w-12 text-brand-primary mb-4" />
          <h3 className="text-xl font-medium text-brand-basic mb-2">
            {linkedConnectors.length > 0 ? "Connection Established" : "Ready to Connect?"}
          </h3>
          <p className="text-center text-gray-600 mb-6 max-w-md">
            {connectionMessage}
          </p>
          {isLoading ? <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin text-brand-primary" />
            <span>Loading connections...</span>
          </div> : <button type="button" onClick={handleOpenModal} className="form-button flex items-center">
            {buttonText}
          </button>}
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button type="button" onClick={onBack} className="form-button-outline">
          Back to Company Info
        </button>
        <DataConnectionFormTooltip
          connectorIncomplete={connectorIncomplete}
          linkedConnectorsLength={linkedConnectors.length}
          isSubmitting={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </> : <>
            <Save className="mr-2 h-4 w-4" />
            Finish
          </>}
        </DataConnectionFormTooltip>
      </div>

      <ConnectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleUpdateConnectors}
        linkedConnectors={linkedConnectors}
        onRemoveConnector={handleRemoveConnector}
        onConnectStart={() => setHasAttemptedConnection(true)}
        isLoadingConnections={isLoadingConnections}
        isLoadingAfterRedirect={isLoadingAfterRedirect}
        onRefreshLinkedConnectors={loadAllConnections}
        onBeginConnectionsRefresh={beginConnectionsRefresh}
        organisationId={resolveOrganisationId()}
      />

    </div>
  );
};

export default DataConnectionForm;
